/**
 * Unified Operations queue page — /staff-ops/:track
 * Same layout for Employment, Landlord, and Document Validation.
 */
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Clock, Search, PhoneCall, RotateCcw, CheckCircle2 } from "lucide-react";
import { fmtDate } from "@/lib/dateFormat";

const TRACK_LABEL = { employment: "Employment Verification", landlord: "Landlord Verification", documents: "Document Validation" };
const PRIO_TONE = { high: "bg-rose-100 text-rose-800", normal: "bg-slate-100 text-slate-700", low: "bg-slate-50 text-slate-500" };
const STATUS_TONE = {
  unassigned: "bg-slate-100 text-slate-600",
  assigned: "bg-blue-100 text-blue-800",
  in_progress: "bg-indigo-100 text-indigo-800",
  awaiting_applicant: "bg-amber-100 text-amber-800",
  awaiting_employer: "bg-amber-100 text-amber-800",
  awaiting_landlord: "bg-amber-100 text-amber-800",
  awaiting_documents: "bg-amber-100 text-amber-800",
  needs_review: "bg-purple-100 text-purple-800",
  completed: "bg-emerald-100 text-emerald-800",
};

export default function OperationsQueuePage() {
  const { track } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [subscriber, setSubscriber] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("oldest");
  const [view, setView] = useState("active");
  const [loading, setLoading] = useState(true);
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient.get(`/staff-ops/queue/${track}`, { params: { q, subscriber, filter, sort, view, limit: 200 } })
      .then(({ data }) => { setRows(data.rows || []); setTotal(data.total || 0); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [track, q, subscriber, filter, sort, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const doReopenWork = async () => {
    if (!reopenTarget || reopenReason.trim().length < 3) return;
    setBusy(true);
    try {
      await apiClient.post(`/staff-ops/case/${reopenTarget.screening_id}/${track}/reopen-work`, { reason: reopenReason.trim() });
      toast.success(`Reopened for more work — ${reopenTarget.applicant_name}`);
      setReopenTarget(null); setReopenReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reopen");
    } finally { setBusy(false); }
  };

  const label = TRACK_LABEL[track] || track;

  return (
    <AppShell>
      <div className="space-y-6" data-testid={`ops-queue-${track}`}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-overline text-slate-500 mb-2">Operations · Queue</div>
            <h1 className="ntn-h2">{label}</h1>
            <p className="ntn-body text-slate-500 mt-1">{total} case{total === 1 ? "" : "s"} in this view</p>
          </div>
          <Link to="/staff-ops" className="text-sm text-slate-500 hover:text-slate-800" data-testid="ops-queue-back">← Operations dashboard</Link>
        </div>

        <Card className="p-4 bg-white border-slate-200">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex gap-2 mr-2">
              <Button size="sm" variant={view === "active" ? "default" : "outline"}
                      onClick={() => setView("active")} data-testid="ops-queue-view-active">Active</Button>
              <Button size="sm" variant={view === "completed" ? "default" : "outline"}
                      onClick={() => setView("completed")} data-testid="ops-queue-view-completed">Completed</Button>
            </div>
            <div className="relative flex-1 min-w-[240px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search applicant or property…"
                     className="pl-9" data-testid="ops-queue-search" />
            </div>
            <div className="relative w-56">
              <Input value={subscriber} onChange={(e) => setSubscriber(e.target.value)}
                     placeholder="Subscriber: All Subscribers"
                     data-testid="ops-queue-subscriber-filter" />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44" data-testid="ops-queue-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All open</SelectItem>
                <SelectItem value="mine">Assigned to me</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                <SelectItem value="awaiting">Awaiting response</SelectItem>
                <SelectItem value="needs_review">Needs review</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-44" data-testid="ops-queue-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="oldest">Oldest first (FIFO)</SelectItem>
                <SelectItem value="newest">Newest first</SelectItem>
                {/* Priority sort retired 2026-02 alongside the priority field. */}
                <SelectItem value="due">Due date</SelectItem>
                <SelectItem value="attempts">Most attempts</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        <Card className="bg-white border-slate-200 divide-y">
          {loading && <div className="p-6 text-slate-500 text-sm">Loading…</div>}
          {!loading && rows.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No cases match.</div>}
          {!loading && rows.map((r) => (
            <div
              key={r.screening_id}
              className="p-4 hover:bg-slate-50 cursor-pointer flex flex-wrap items-center gap-4"
              onClick={() => navigate(`/staff-ops/${track}/${r.screening_id}`)}
              data-testid={`ops-queue-row-${r.screening_id}`}
            >
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2">
                  {r.is_new && view === "active" && (
                    <Badge className="bg-rose-600 text-white animate-pulse font-bold tracking-wide" data-testid={`ops-queue-new-badge-${r.screening_id}`}>NEW</Badge>
                  )}
                  <span className="font-semibold text-slate-900">{r.applicant_name || "—"}</span>
                </div>
                <div className="text-xs text-slate-500 truncate">{r.property_address}</div>
                {view === "completed" && r.completed_at && (
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    Completed {fmtDate(r.completed_at)}{r.completed_by_name ? ` by ${r.completed_by_name}` : ""}
                  </div>
                )}
              </div>
              {view === "completed" && r.outcome ? (
                <Badge className="bg-emerald-100 text-emerald-800" data-testid={`ops-queue-outcome-${r.screening_id}`}>
                  <CheckCircle2 className="h-3 w-3 mr-1" />{r.outcome.replace(/_/g, " ")}
                </Badge>
              ) : (
                <Badge className={STATUS_TONE[r.status] || "bg-slate-100"}>{r.status?.replace(/_/g, " ")}</Badge>
              )}
              {/* Priority badge retired 2026-02 — only rendered for historical rows that still carry one. */}
              {r.priority && (
                <Badge variant="outline" className={PRIO_TONE[r.priority]} data-testid={`ops-queue-legacy-priority-${r.screening_id}`}>{r.priority}</Badge>
              )}
              <div className="text-xs text-slate-500 w-40 truncate">
                {r.assigned_staff_name ? `Assigned · ${r.assigned_staff_name}` : "Unassigned"}
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500 w-24">
                <PhoneCall className="h-3.5 w-3.5" /> {r.attempts_count} attempt{r.attempts_count === 1 ? "" : "s"}
              </div>
              {r.overdue && (
                <div className="flex items-center gap-1 text-xs text-rose-700 font-semibold">
                  <AlertTriangle className="h-3.5 w-3.5" /> Overdue
                </div>
              )}
              {r.due_at && !r.overdue && (
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <Clock className="h-3.5 w-3.5" /> Due {fmtDate(r.due_at)}
                </div>
              )}
              {view === "completed" && (
                <Button
                  size="sm" variant="outline"
                  onClick={(e) => { e.stopPropagation(); setReopenTarget(r); }}
                  data-testid={`ops-queue-reopen-work-${r.screening_id}`}
                >
                  <RotateCcw className="h-4 w-4 mr-1.5" /> Reopen for work
                </Button>
              )}
            </div>
          ))}
        </Card>

        {/* Reopen-for-work dialog (Phase F — non-destructive) */}
        <Dialog open={!!reopenTarget} onOpenChange={(o) => { if (!o) { setReopenTarget(null); setReopenReason(""); } }}>
          <DialogContent data-testid="ops-queue-reopen-dialog">
            <DialogHeader>
              <DialogTitle>Reopen for more work</DialogTitle>
              <DialogDescription>
                {reopenTarget?.applicant_name} · {label} — returns this verification to the active queue.
                Findings, the published PM summary, risk indicators and manual risk banners are preserved.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              placeholder="Reason for reopening (required)…"
              maxLength={500}
              data-testid="ops-queue-reopen-reason"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setReopenTarget(null); setReopenReason(""); }} data-testid="ops-queue-reopen-cancel">Cancel</Button>
              <Button onClick={doReopenWork} disabled={busy || reopenReason.trim().length < 3} data-testid="ops-queue-reopen-confirm">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Reopen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
